class ProductFlavorSelector extends HTMLElement {
  constructor() {
    super();
    /**
     * DOM Elements
     */
    this.checkboxes = this.querySelectorAll("[data-flavor-checkbox]");
    this.instructions = this.querySelector("[data-flavor-instructions]");
    this.errorElement = this.querySelector("[data-flavor-error]");

    /**
     * Rules & Options
     * - rules: Array of parsed variant rules that determine max flavor selections
     * - optionTypes: Option positions (numbers) or legacy names (e.g., "Size")
     */
    this.rules = this.parseRules(this.dataset.variantRules);
    this.optionTypes = new Set();
    this.rules.forEach((rule) => {
      rule.conditions.forEach((condition) => {
        this.optionTypes.add(condition.option);
      });
    });

    /**
     * State Management
     * - selectedOptions: Tracks value positions or legacy text (e.g., { 1: 3, Size: "50-piece Box" })
     *   Used to determine which rule applies and therefore how many flavors can be selected
     *
     * - selectionQueue: Maintains FIFO order of flavor checkbox selections
     *   When max selections is reached, oldest selection is removed first
     *   Example: If max=2 and user selects [A,B,C], queue will be [B,C] (A was removed)
     */
    this.selectedOptions = {};
    this.selectionQueue = [];
    this.maxSelections = 1; // Default
    this.minSelections = 1; // Always require at least 1

    // All customer-facing copy arrives from Liquid's locale payload. Empty
    // defaults keep an incomplete fixture fail-closed instead of smuggling a
    // second, untranslated English source into the runtime.
    this.containsLabel = this.dataset.allergenContainsLabel || "";
    this.allergenMissingTemplate = this.dataset.allergenMissing || "";
    this.containsBothLabel = this.dataset.allergenContainsBothLabel || "";
    this.containsFlavorLabel = this.dataset.allergenContainsFlavorLabel || this.containsLabel;
    this.errorTooMany = this.dataset.errorTooMany || "";
    this.errorTooFew = this.dataset.errorTooFew || "";
    this.selectIngredientsMessage = this.dataset.selectIngredients || "";
    this.ingredientMissingTemplate = this.dataset.ingredientMissing || "";

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.initialize());
    } else {
      this.initialize();
    }
  }

  initialize() {
    this.initialized = false;
    // Initialize variant selectors
    this.initializeVariantSelectors();
    
    // Set up variant change listeners
    this.setupVariantListeners();
    
    // Initialize max selections based on current variant
    this.updateSelectionsBasedOnVariant();
    
    // Set up flavor checkbox listeners
    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", this.handleFlavorChange.bind(this));
    });

    // Ensure at least one flavor is selected by default
    this.ensureMinimumSelection();

    // Load the flavor → ingredients map from the embedded JSON script tag
    this.loadIngredientsMap();

    const disclosure = this.findIngredientsTarget()?.closest("details");
    if (disclosure) {
      const syncExpanded = () => disclosure.querySelector("summary")?.setAttribute("aria-expanded", String(disclosure.open));
      disclosure.addEventListener("toggle", syncExpanded);
      syncExpanded();
    }

    // Paint the ingredients section once on load to match the default flavor
    this.updateIngredientsDisplay();

    // Intercept form submission
    this.setupFormSubmission();
    this.initialized = true;
  }

  /**
   * Read the flavor → ingredient HTML map emitted by
   * snippets/product-flavor-selector.liquid. Stored as JSON inside a
   * <script type="application/json" data-flavor-ingredients-map> tag.
   * Each value is the rendered HTML of the flavor product's ingredients
   * metafield (custom.ingredients on each flavor product — the canonical
   * allergen key, written by Nicole in Shopify Admin; the theme is a pure
   * reader. See METHOD-VERDICT-2026-08-06 recursive ruling R2).
   */
  loadIngredientsMap() {
    const readMap = (attribute) => {
      try {
        const element = this.querySelector(`script[${attribute}]`);
        const value = element && JSON.parse(element.textContent);
        return value && typeof value === "object" && !Array.isArray(value) ? value : null;
      } catch {
        return null;
      }
    };
    this.ingredientsMap = readMap("data-flavor-ingredients-map") || {};
    this.containsMap = readMap("data-flavor-contains-map");
    this.flavorUrlMap = readMap("data-flavor-url-map");
  }

  findIngredientsTarget() {
    const productRoot = this.closest('.main-product, [data-product-root], section');
    return productRoot?.querySelector("[data-flavor-ingredients-target]") || null;
  }

  flavorIngredients({ id, label }, showLabel) {
    const content = document.createDocumentFragment();
    if (showLabel) {
      const heading = document.createElement("p");
      const strong = document.createElement("strong");
      strong.textContent = label;
      heading.append(strong);
      content.append(heading);
    }
    const paragraph = document.createElement("p");
    const ingredients = this.ingredientsMap?.[id];
    if (ingredients) {
      paragraph.textContent = ingredients;
    } else {
      const fallback = document.createElement("em");
      fallback.textContent = this.formatCopy(this.ingredientMissingTemplate, { flavor: label });
      paragraph.append(fallback);
    }
    content.append(paragraph);
    return content;
  }

  updateIngredientsDisplay() {
    const selectedFlavors = Array.from(this.checkboxes)
      .filter((cb) => cb.checked)
      .map((cb) => ({ id: cb.value, label: cb.dataset.flavorLabel || cb.value }));
    const target = this.findIngredientsTarget();
    const disclosure = target?.closest("details");
    const productRoot = this.closest('.main-product, [data-product-root], section');
    const allergenTarget = productRoot?.querySelector('[data-flavor-allergens-target]');

    if (target) {
      const content = document.createDocumentFragment();
      if (!selectedFlavors.length) {
        const message = document.createElement("p");
        message.textContent = this.formatCopy(this.selectIngredientsMessage);
        content.append(message);
      } else {
        selectedFlavors.forEach(flavor => content.append(this.flavorIngredients(flavor, selectedFlavors.length > 1)));
      }
      target.replaceChildren(content);
      if (this.initialized && disclosure && selectedFlavors.length) {
        disclosure.open = true;
        disclosure.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      }
    }
    if (allergenTarget) this.updateAllergensDisplay(selectedFlavors, allergenTarget);
  }

  updateAllergensDisplay(selectedFlavors, target) {
    const content = document.createDocumentFragment();
    const containsValues = selectedFlavors.map(({ id }) =>
      typeof this.containsMap?.[id] === "string" ? this.containsMap[id].trim() : "");
    const appendContains = (value, label, union = false) => {
      const paragraph = document.createElement("p");
      paragraph.className = "product-allergens__contains";
      if (union) paragraph.setAttribute("data-flavor-allergens-union", "");
      const strong = document.createElement("strong");
      strong.textContent = label;
      paragraph.append(strong, document.createTextNode(` ${value}`));
      content.append(paragraph);
    };
    selectedFlavors.forEach((flavor, index) => {
      const { id, label } = flavor;
      const value = containsValues[index];
      if (value) {
        appendContains(value, selectedFlavors.length > 1
          ? this.formatCopy(this.containsFlavorLabel, { flavor: label }) : this.containsLabel);
      } else {
        const paragraph = document.createElement("p");
        paragraph.className = "product-allergens__fallback";
        paragraph.setAttribute("data-flavor-allergens-fallback", "");
        if (this.flavorUrlMap?.[id]) {
          const [before, after] = this.allergenMissingTemplate.split("{flavor}");
          const link = document.createElement("a");
          link.href = this.flavorUrlMap[id];
          link.textContent = label;
          paragraph.append(document.createTextNode(before), link, document.createTextNode(after || ""));
        } else {
          paragraph.textContent = this.formatCopy(this.allergenMissingTemplate, { flavor: label });
        }
        content.append(paragraph);
      }
    });
    if (selectedFlavors.length > 1 && containsValues.every(Boolean)) {
      const seen = new Set();
      const union = [];
      containsValues.forEach(value => {
        value.split(",").map(term => term.trim()).filter(Boolean).forEach(term => {
          const key = term.toLowerCase();
          if (!seen.has(key)) { seen.add(key); union.push(term); }
        });
      });
      appendContains(union.join(", "), this.containsBothLabel, true);
    }
    target.replaceChildren(content);
  }

  initializeVariantSelectors() {
    this.variantSelectors = {};
    this.optionTypes.forEach((optionType) => {
      const positional = typeof optionType === "number";
      const radioSelector = positional
        ? `input[type="radio"][data-option-position="${optionType}"]`
        : `input[type="radio"][name*="${optionType}"]`;
      const selector = document.querySelector(`${radioSelector}:checked`) ||
        document.querySelector(radioSelector);
      
      if (selector) {
        if (!selector.checked) selector.checked = true;
        this.variantSelectors[optionType] = selector;
        this.selectedOptions[optionType] = positional
          ? Number(selector.dataset.valuePosition) : selector.dataset.optionValue;
      }
    });
  }

  setupVariantListeners() {
    document.querySelectorAll('input[type="radio"]').forEach((radio) => {
      const optionTypes = Array.from(this.optionTypes).filter((type) =>
        typeof type === "number"
          ? Number(radio.dataset.optionPosition) === type : radio.name.includes(type)
      );

      if (optionTypes.length) {
        radio.addEventListener("change", (event) => {
          optionTypes.forEach((optionType) => {
            this.selectedOptions[optionType] = typeof optionType === "number"
              ? Number(event.target.dataset.valuePosition) : event.target.dataset.optionValue;
          });
          this.updateSelectionsBasedOnVariant();
        });
      }
    });
  }

  setupFormSubmission() {
    const carrier = this.querySelector('[data-flavor-joined]');
    const form = carrier?.form || document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form || form.dataset.flavorSubmitBound === "true") return;
    form.dataset.flavorSubmitBound = "true";
    this.cartForm = form;

    // Keep the hidden flavor input current so product-form.js's own AJAX FormData
    // picks it up. Do NOT cloneNode/replaceChild the form: cloneNode does not copy
    // event listeners, so replacing the form strips product-form's submit handler —
    // which drops the AJAX cart-drawer add AND the gift-config interception, forcing
    // a native full-page POST. (Verified HIGH bug, theme buy-flow hunt 2026-06-25.)
    this.syncFlavorInput();

    // Capture phase: an invalid selection blocks product-form's (bubble-phase) AJAX
    // add via stopImmediatePropagation; on a valid selection we re-sync the flavor
    // input and let product-form's own submit handler run intact.
    form.addEventListener(
      "submit",
      (event) => {
        const validation = this.validateSelection();
        if (!validation.isValid) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showError(validation.error);
          return;
        }
        this.syncFlavorInput();
        this.hideError();
      },
      true,
    );
  }

  // Keep the server-rendered properties[Flavor] carrier current so it is already
  // in both product-form's AJAX FormData and a native no-JS form POST.
  syncFlavorInput() {
    const joined =
      this.querySelector('[data-flavor-joined]') ||
      this.cartForm?.querySelector('input[name="properties[Flavor]"][data-flavor-joined]');
    const form = joined?.form || this.cartForm;
    if (!form || !joined) return;
    const flavors = this.checkedFlavors.map((cb) => cb.value);

    // Liquid renders exactly one form-associated carrier for every flavor
    // selector. The checkboxes are UI-only; this method only updates that
    // server-owned control. Creating, deleting, or duplicating it in JavaScript
    // would reopen the no-script hole this shape fixed.

    // An empty selection leaves the server-rendered default in place rather
    // than blanking it. The floor in handleFlavorChange makes zero-checked
    // unreachable from the UI, so reaching here means something upstream
    // failed — and a stale-but-real flavor is a recoverable order, while a
    // blank one is the unfulfillable line that started this (TSC01617).
    if (!flavors.length) return;

    joined.value = flavors.join(', ');
  }

  parseRules(rulesText) {
    if (!rulesText) return [];
    
    return rulesText.split("\n").map((line) => {
      const [conditionsPart, countPart] = line.split(":");
      const conditions = conditionsPart.split(",").map((condition) => {
        const match = condition.match(/(\w+)\[(.*?)\]/);
        if (!match) return null;
        const option = match[1].trim();
        const value = match[2].trim();
        // 1[3] addresses option 1, value 3, independently of display translations.
        return /^[1-9]\d*$/.test(option)
          ? { option: Number(option), value: Number(value) } : { option, value };
      }).filter(Boolean);
      
      // Guard a malformed rule line (missing "=", non-numeric, <= 0). Without this a
      // NaN count flows to maxSelections, and `length > NaN` is always false, so max-
      // selection enforcement silently breaks (a customer could pick unlimited flavors
      // on a fixed-count box). Fail safe to 1 (the minimum) instead.
      const parsed = parseInt((countPart || "").split("=")[1], 10);
      const count = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
      return { conditions, count };
    });
  }

  formatCopy(template, values = {}) {
    return String(template || "").replace(/\{\{\s*(\w+)\s*\}\}|\{(\w+)\}/g, (match, doubleKey, singleKey) => {
      const key = doubleKey || singleKey;
      return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
    });
  }

  updateSelectionsBasedOnVariant() {
    // Find matching rule for current variant
    const matchingRule = this.rules.find((rule) => {
      return rule.conditions.every((condition) => {
        return this.selectedOptions[condition.option] === condition.value;
      });
    });

    this.maxSelections = matchingRule ? matchingRule.count : 1;

    // Update UI
    const maxCountElement = this.querySelector("[data-max-count]");
    if (maxCountElement) {
      maxCountElement.textContent = this.maxSelections;
    }

    // Enforce max selections on existing checkboxes
    this.enforceMaxSelections();
  }

  // The merchant-authored "too few" message, with {min} substituted the way
  // validateSelection() already substitutes {max} into errorTooMany.
  //
  // Before this, `this.errorTooFew` was computed in the constructor and read by
  // NOBODY: both refusal paths hardcoded their own English literal, so a message
  // Nicole typed into the theme editor was silently discarded and {min} was
  // never replaced. Three strings for one idea, only one of them reachable.
  get tooFewMessage() {
    return this.formatCopy(this.errorTooFew, { min: 1 });
  }

  // Every flavor read goes through here, so "which boxes are checked" has one
  // implementation rather than seven copies of the same Array.from(...).filter.
  get checkedFlavors() {
    return Array.from(this.checkboxes).filter((cb) => cb.checked);
  }

  handleFlavorChange(event) {
    if (event.target.checked) {
      // Add to queue
      this.selectionQueue.push(event.target);
      
      // Enforce max selections
      if (this.selectionQueue.length > this.maxSelections) {
        const removed = this.selectionQueue.shift();
        removed.checked = false;
      }
    } else {
      // Unchecking the LAST flavor is refused, not merely warned about.
      //
      // product-form.js gates the AJAX add-to-cart path, but a native no-JS
      // POST never runs that guard. The only way to cover every route is to
      // make the zero-flavor state unreachable in the first place, which is
      // what this does.
      //
      // This mirrors ensureMinimumSelection(), which already guarantees one
      // checked box on upgrade. Without it, that guarantee lasted exactly until
      // the customer's first click.
      if (this.checkedFlavors.length === 0) {
        event.target.checked = true;
        this.selectionQueue = [event.target];
        // The attempted state is repaired immediately, so showing “select at
        // least one” while one is visibly selected is a false validation error.
        // Keep the invariant and make the redundant tap a silent no-op.
        this.hideError();
        return;
      }
      // Remove from queue
      this.selectionQueue = this.selectionQueue.filter(cb => cb !== event.target);
    }

    // Clear any errors when user is actively selecting
    this.hideError();

    // Refresh both disclosures from the selected products' metafields.
    this.updateIngredientsDisplay();

    // Keep the hidden flavor input in sync so it's current in product-form's FormData.
    this.syncFlavorInput();

    // Track flavor selection
    if (window.SnowcubesAnalytics) {
      const normalizeFlavor = (value) =>
        String(value || "")
          .replace(/[\u0000-\u001f\u007f]/g, " ")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 40);
      const selectedFlavors = this.selectionQueue
        .map((checkbox) => normalizeFlavor(checkbox.value))
        .filter(Boolean)
        .slice(0, 4);
      window.SnowcubesAnalytics.track(
        window.SnowcubesAnalytics.EVENTS.FLAVOR_SELECT,
        {
          product_id: this.dataset.product || null,
          selected_flavors: selectedFlavors,
          flavor_selection_count: selectedFlavors.length,
          max_selections: this.maxSelections,
          action: event.target.checked ? 'add' : 'remove',
          flavor: normalizeFlavor(event.target.value),
          selection_source: 'pdp_flavor_picker',
        }
      );
    }
  }

  enforceMaxSelections() {
    const currentlyChecked = this.checkedFlavors;
    
    if (currentlyChecked.length > this.maxSelections) {
      // Keep the shopper's most recent picks. Oldest first: checked boxes with
      // no recorded pick (page order), then the selection queue's pick order.
      const ordered = [...new Set([
        ...currentlyChecked.filter((cb) => !this.selectionQueue.includes(cb)),
        ...this.selectionQueue.filter((cb) => cb.checked),
      ])];
      const toUncheck = ordered.length - this.maxSelections;
      ordered.slice(0, toUncheck).forEach((cb) => { cb.checked = false; });

      // The queue keeps pick order for the boxes that remain checked.
      this.selectionQueue = ordered.slice(toUncheck);

      // Variant changes can trim an existing selection without firing a flavor
      // checkbox change event. Keep the cart property and ingredient disclosure
      // aligned with the checkboxes that remain selected.
      this.syncFlavorInput();
      this.updateIngredientsDisplay();
    }
  }

  ensureMinimumSelection() {
    const hasSelection = this.checkedFlavors.length > 0;

    if (!hasSelection && this.checkboxes.length > 0) {
      this.checkboxes[0].checked = true;
      this.selectionQueue = [this.checkboxes[0]];
    } else {
      // Initialize queue with existing selections
      this.selectionQueue = this.checkedFlavors;
    }
  }

  validateSelection() {
    const selected = this.checkedFlavors;
    const selectedFlavors = selected.map(cb => cb.value);
    
    // Must have at least one flavor
    if (selected.length === 0) {
      return {
        isValid: false,
        error: this.tooFewMessage
      };
    }
    
    // Can't have more than max
    if (selected.length > this.maxSelections) {
      return {
        isValid: false,
        error: this.formatCopy(this.errorTooMany, { max: this.maxSelections })
      };
    }
    
    // For now, we allow any number from 1 to max (flexible selection)
    // If you need exact selection (must be exactly max), uncomment below:
    /*
    if (selected.length !== this.maxSelections && this.maxSelections > 1) {
      return {
        isValid: false,
        error: `Please select exactly ${this.maxSelections} flavors`
      };
    }
    */
    
    return {
      isValid: true,
      flavors: selectedFlavors
    };
  }

  showError(message) {
    if (this.errorElement) {
      const errorMessage = this.errorElement.querySelector(".error-message");
      if (errorMessage) {
        errorMessage.textContent = message;
      }
      this.errorElement.style.display = "block";
    }
  }

  hideError() {
    if (this.errorElement) {
      this.errorElement.style.display = "none";
    }
  }
}

customElements.define("product-flavor-selector", ProductFlavorSelector);
